import { useCallback, useMemo } from 'react';
import { useSAL2050_R01Store } from './Store_SAL2050_R01';
import { SAL2050_R01TabType } from './Store_SAL2050_R01';
import { getCustomerSalesColumns, getItemSalesColumns } from './components/ColumnDefinitions';
import { ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';
import { downloadSettingExcel } from 'src/utils/blob/blobUtil';
import { exportSAL2050_R01Excel } from './Api_SAL2050_R01';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { SAL2050_R01_TABS, dedupeRowsByTab, getTabDisplayName, getViewType } from './utils/tabConfig';

interface SAL2050ContentProps {
	setMessage: (message: string) => void;
}

export const Content_SAL2050_R01 = (props: SAL2050ContentProps) => {
	const { setMessage } = props;

	const {
		SAL2050_R01Filter,
		SAL2050_R01activeTab,
		SAL2050_R01tabData,
		setSAL2050_R01ActiveTab,
		setSAL2050_R01TabData,
		setSAL2050_R01TabSelectedIds,
		setSAL2050_R01TabLoadingState,
		resetAllSAL2050_R01TabScrollTop,
		setSAL2050_R01TabScrollTop,
	} = useSAL2050_R01Store();

	const { setLoadingSpinner } = useLoadingSpinnerStore();

	const { columns, data } = useMemo(() => {
		switch (SAL2050_R01activeTab) {
			case 'item':
				return { columns: getItemSalesColumns(), data: SAL2050_R01tabData.item };
			case 'customer':
			default:
				return { columns: getCustomerSalesColumns(), data: SAL2050_R01tabData.customer };
		}
	}, [SAL2050_R01activeTab, SAL2050_R01tabData.customer, SAL2050_R01tabData.item]);

	const changeTabAndFetch = useCallback((tab: SAL2050_R01TabType, options: { clearData?: boolean; clearSelection?: boolean } = {}) => {
		const { clearData = true, clearSelection = true } = options;

		setSAL2050_R01ActiveTab(tab);

		if (clearData) {
			setSAL2050_R01TabData(tab, []);
		}
		if (clearSelection) {
			setSAL2050_R01TabSelectedIds(tab, []);
		}
	}, [setSAL2050_R01ActiveTab, setSAL2050_R01TabData, setSAL2050_R01TabSelectedIds]);

	const fetchData = useCallback(async () => {
		resetAllSAL2050_R01TabScrollTop();
		const tabs: SAL2050_R01TabType[] = SAL2050_R01_TABS.map(tab => tab.id);

		const { getSAL2050_R01Data } = await import('./Api_SAL2050_R01');

		const tabCounts: { tab: SAL2050_R01TabType; count: number }[] = [];

		await Promise.all(tabs.map(async (tab) => {
			try {
				setSAL2050_R01TabLoadingState(tab, true);

				const viewType = getViewType(tab);
				const res = await getSAL2050_R01Data(viewType, SAL2050_R01Filter);

				if (res?.data?.data && Array.isArray(res.data.data)) {
					const validData = res.data.data.filter((item: any) => item && typeof item === 'object');
					const uniqueData = dedupeRowsByTab(tab, validData);

					if (uniqueData.length > 0) {
						setSAL2050_R01TabData(tab, uniqueData);
						tabCounts.push({ tab, count: uniqueData.length });
					} else {
						console.warn(`${tab} 탭: 처리 후 유효한 데이터가 없습니다.`);
						setSAL2050_R01TabData(tab, []);
						tabCounts.push({ tab, count: 0 });
					}
				} else {
					console.warn(`${tab} 탭: 응답 데이터가 없거나 잘못된 형식입니다.`, res);
					setSAL2050_R01TabData(tab, []);
					tabCounts.push({ tab, count: 0 });
				}
			} catch (e) {
				console.error(`${tab} 탭 데이터 조회 실패:`, e);
				setSAL2050_R01TabData(tab, []);
				tabCounts.push({ tab, count: 0 });
			} finally {
				setSAL2050_R01TabLoadingState(tab, false);
			}
		}));

		const totalCount = tabCounts.reduce((sum, item) => sum + item.count, 0);

		if (totalCount > 0) {
			ShowSuccessFetch(setMessage, `전년대비실적 조회가 완료되었습니다. (총 ${totalCount}건)`);
			setTimeout(() => setMessage(""), 5000);
		} else {
			showWarning('데이터가 없습니다.');
		}
	}, [setSAL2050_R01TabData, setSAL2050_R01TabLoadingState, SAL2050_R01Filter, setMessage, resetAllSAL2050_R01TabScrollTop]);

	const fetchSingleTabData = useCallback(async (tab: SAL2050_R01TabType) => {
		setSAL2050_R01TabScrollTop(tab, null);
		setLoadingSpinner(true);
		try {
			setSAL2050_R01TabLoadingState(tab, true);

			const { getSAL2050_R01Data } = await import('./Api_SAL2050_R01');
			const viewType = getViewType(tab);
			const res: any = await getSAL2050_R01Data(viewType, SAL2050_R01Filter);

			if (res?.data?.data && Array.isArray(res.data.data)) {
				const validData = res.data.data.filter((item: any) => item && typeof item === 'object');
				const uniqueData = dedupeRowsByTab(tab, validData);

				if (uniqueData.length > 0) {
					setSAL2050_R01TabData(tab, uniqueData);
					ShowSuccessFetch(setMessage, `${getTabDisplayName(tab)} 조회가 완료되었습니다. (총 ${uniqueData.length}건)`);
					setTimeout(() => setMessage(""), 5000);
				} else {
					console.warn(`${tab} 탭: 처리 후 유효한 데이터가 없습니다.`);
					setSAL2050_R01TabData(tab, []);
					showWarning(`${getTabDisplayName(tab)} 데이터가 없습니다.`);
				}
			} else {
				console.warn(`${tab} 탭: API 응답 데이터가 없거나 잘못된 형식입니다.`, res);
				setSAL2050_R01TabData(tab, []);
				showWarning(`${getTabDisplayName(tab)} 데이터가 없습니다.`);
			}
		} catch (e) {
			console.error(`${tab} 탭 데이터 조회 실패:`, e);
			setSAL2050_R01TabData(tab, []);
			showWarning(`${getTabDisplayName(tab)} 조회 중 오류가 발생했습니다.`);
		} finally {
			setSAL2050_R01TabLoadingState(tab, false);
			setLoadingSpinner(false);
		}
	}, [setSAL2050_R01TabData, setSAL2050_R01TabLoadingState, SAL2050_R01Filter, setMessage, setSAL2050_R01TabScrollTop, setLoadingSpinner]);

	const handleTabChange = useCallback(async (tab: SAL2050_R01TabType) => {
		try {
			changeTabAndFetch(tab, { clearData: true, clearSelection: true });
			await fetchSingleTabData(tab);
		} catch (error) {
			console.error('탭 변경 중 오류:', error);
		}
	}, [changeTabAndFetch, fetchSingleTabData]);

	const handleExcelClick = useCallback(async () => {
		try {
			const state = useSAL2050_R01Store.getState();
			const activeTab = state.SAL2050_R01activeTab;
			const filter = state.SAL2050_R01Filter;
			const viewType = getViewType(activeTab);
			const response = await exportSAL2050_R01Excel(viewType, { ...filter });

			if (response?.data?.data) {
				downloadSettingExcel({
					responseData: response.data.data,
					fileName: `전년대비실적조회_${getTabDisplayName(activeTab)}.xlsx`
				});
			} else {
				throw new Error('서버에서 올바른 엑셀 데이터를 받지 못했습니다.');
			}
		} catch (error) {
			console.error('Excel 내보내기 실패:', error);

			if (error instanceof Error) {
				showWarning(`엑셀 내보내기 실패: ${error.message}`);
			} else {
				showWarning('엑셀 내보내기 중 오류가 발생했습니다.');
			}
		}
	}, []);

	return {
		fetchData,
		handleExcelClick,
		handleTabChange,
		columns,
		data
	};
};
